import type { WhyPoint } from "@shared/labs/level-assessment";
import { ArrowUp, Check, ChevronUp, Minus } from "lucide-react";
import { LevelChip } from "./chips";
import { C, DM, UI } from "./ui-tokens";

export interface WhyData {
  satisfied: { level: string; items: WhyPoint[] };
  /** Why NOT one level higher. */
  up: { level: string; items: WhyPoint[] };
  /** Why the candidate clears the level below (the floor). */
  down: { level: string; items: WhyPoint[] };
}

type ColumnKind = "satisfied" | "up" | "down";

function WhyColumn({
  kind,
  level,
  items,
  anchor = false,
}: {
  kind: ColumnKind;
  level: string;
  items: WhyPoint[];
  anchor?: boolean;
}) {
  const cfg = {
    satisfied: { title: `Por que ${level}`, color: C.green },
    up: { title: `Por que não ${level}`, color: C.amberFg },
    down: { title: `Por que não ${level}`, color: C.ink50 },
  }[kind];

  const HeaderIcon = kind === "up" ? ArrowUp : ChevronUp;
  const ItemIcon = kind === "satisfied" ? Check : kind === "up" ? Minus : ArrowUp;
  const itemColor = kind === "satisfied" ? C.green : kind === "up" ? C.amberFg : C.ink50;

  return (
    <div
      style={{
        background: anchor ? C.white : "transparent",
        border: anchor ? `1px solid ${C.border}` : "none",
        borderTop: anchor ? `3px solid ${C.green}` : "none",
        borderRadius: anchor ? 14 : 0,
        padding: anchor ? "20px 20px 22px" : "20px 4px",
        boxShadow: anchor ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {kind === "satisfied" ? (
          <LevelChip level={level} state="assigned" />
        ) : (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 22,
              height: 22,
              borderRadius: 999,
              background: kind === "up" ? C.amberBg : C.ink5,
            }}
          >
            <HeaderIcon size={15} color={cfg.color} />
          </span>
        )}
        <span
          style={{
            ...DM,
            fontWeight: 700,
            fontSize: 15,
            color: anchor ? C.ink : C.ink70,
            letterSpacing: "-0.01em",
          }}
        >
          {cfg.title}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
        {items.length === 0 ? (
          <div style={{ ...UI, fontSize: 12.5, color: C.ink50, lineHeight: 1.5 }}>
            Sem pontos registrados.
          </div>
        ) : (
          items.map((it) => (
            <div key={`${it.point}|${it.evidence}`} style={{ display: "flex", gap: 9 }}>
              <span style={{ marginTop: 2, flex: "none", display: "inline-flex" }}>
                <ItemIcon size={15} color={itemColor} />
              </span>
              <div>
                <div
                  style={{ ...DM, fontSize: 13.5, fontWeight: 600, color: C.ink, lineHeight: 1.4 }}
                >
                  {it.point}
                </div>
                {it.evidence && (
                  <div
                    style={{ ...UI, fontSize: 12.5, color: C.ink70, lineHeight: 1.5, marginTop: 3 }}
                  >
                    {it.evidence}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/** Three-column comparative: floor (why not below) · anchored level · ceiling
 *  (why not above). The center column is the visual anchor. */
export function WhyPanel({ data }: { data: WhyData }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1.15fr 1fr",
        gap: 14,
        alignItems: "start",
      }}
    >
      <WhyColumn kind="down" level={data.down.level} items={data.down.items} />
      <WhyColumn
        kind="satisfied"
        level={data.satisfied.level}
        items={data.satisfied.items}
        anchor
      />
      <WhyColumn kind="up" level={data.up.level} items={data.up.items} />
    </div>
  );
}
